import {Button} from "react-bootstrap";
import React from "react";
import {useNavigate} from "react-router-dom";
import {addToCourseCart, enrollInCourse} from "../api/CourseApi";
import {showSuccessToast} from "../utils/usacmUtils";
import {getCoursePriceAccessStr, getCoursePriceAmountStr, isFreeEnrollPrice} from "./courseUtils";

/**
 * The prices of a catalog course, each with its own button: a free price which needs no review enrolls straight
 * away, the others go in the cart.  Used by the catalog cards and the course's own page.
 * @param course a catalog course (see courses_utils.format_catalog_course_display)
 * @param onChanged called after the user enrolled or changed their cart, so the caller can reload
 * @param setErrors called with the errors when enrolling or adding to the cart fails
 */
export function CoursePriceList({course, onChanged, setErrors}) {
  const navigate = useNavigate();

  function callAddToCart(coursePrice) {
    addToCourseCart(coursePrice.id, (status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast(course.cart_price_id ? `Changed the price in your cart.` : `Added "${course.title}" to your cart.`);
        onChanged();
      } else {
        setErrors(newErrors);
      }
    });
  }

  function callEnroll(coursePrice) {
    enrollInCourse(coursePrice.id, (status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast(`You are enrolled in "${course.title}".`);
        onChanged();
      } else {
        setErrors(newErrors);
      }
    });
  }

  function getPriceButton(coursePrice) {
    if (course.has_access || course.pending_review) {
      return null;
    }
    if (isFreeEnrollPrice(coursePrice)) {
      return <Button type="button" onClick={() => callEnroll(coursePrice)}>Enroll</Button>;
    }
    if (course.cart_price_id === coursePrice.id) {
      return <Button type="button" variant="outline-secondary" onClick={() => navigate('/course-cart')}>In Cart</Button>;
    }
    return (
      <Button type="button" onClick={() => callAddToCart(coursePrice)}>
        {course.cart_price_id ? 'Change to This Price' : 'Add to Cart'}
      </Button>
    );
  }

  return (
    <>
      {course.prices.map(coursePrice =>
        <div key={coursePrice.id}
             className="course-catalog-price d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <div className="fw-semibold">{coursePrice.name}</div>
            {coursePrice.description && <div className="small">{coursePrice.description}</div>}
            <div className="small text-muted">
              {getCoursePriceAccessStr(coursePrice)}
              {coursePrice.requires_review && <span> &middot; Requires approval</span>}
              {coursePrice.requires_file && <span> &middot; File upload required</span>}
              {coursePrice.end_date && <span> &middot; Available until {coursePrice.end_date}</span>}
            </div>
          </div>
          <div className="fw-semibold course-catalog-amount">{getCoursePriceAmountStr(coursePrice.amount)}</div>
          {getPriceButton(coursePrice)}
        </div>
      )}
    </>
  );
}
